import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {getWindow} from 'ssr-window';
import IconCheck from '@spothero/icons/check';
import IconExclamationCircle from '@spothero/icons/exclamation-circle';
import {
    Flex,
    Text,
    Box,
    Icon,
    Button,
    Link,
    usePrefersReducedMotion,
    Alert,
} from '@spothero/ui';
import {
    PROPTYPE_RESERVATION_VEHICLE,
    PROPTYPE_RESERVATION_PAYMENT_STATUS,
    PROPTYPE_RESERVATION_PARTNER,
} from '@/proptypes/reservation-prop-types';
import ReservationUtils from 'utils/reservation-utils';
import useVehicleChangeProps from 'pages/parking-pass/hooks/useVehicleChangeProps';
import {useDispatch} from 'react-redux';
import {push} from 'store/router/router-actions';

const containerStyles = {
    borderRadius: 'md',
    borderWidth: '1px',
    borderColor: 'gray.100',
    flexDirection: 'row',
    shadow: 'base',
    marginTop: 4,
    sx: {
        '@media print': {
            shadow: 'none',
        },
    },
};

const textContainerStyles = {
    padding: 4,
};

const leftAreaStyles = {
    padding: 4,
    borderTopLeftRadius: '0.33rem',
    borderBottomLeftRadius: '0.33rem',
};

const topTextStyles = {
    fontSize: 'sm',
    fontWeight: 'semibold',
    lineHeight: '1',
};

const bottomTextStyles = {
    marginTop: 2.5,
};

const NotificationBox = ({
    vehicleRequired,
    vehicle,
    activeStatus,
    onInstructionsClick,
    paymentStatus,
    phoneNumberRequired,
    phoneNumber,
    partner,
}) => {
    const isPhoneOkay =
        !phoneNumberRequired ||
        Boolean(
            phoneNumber && typeof phoneNumber === 'string' && phoneNumber !== ''
        );
    const isVehicleOkay =
        !vehicleRequired ||
        (vehicleRequired && vehicle && Object.keys(vehicle).length > 0);
    const showAllSet = isVehicleOkay && isPhoneOkay;
    const dispatch = useDispatch();
    const buttonProps = useVehicleChangeProps();
    const window = getWindow();
    const prefersReducedMotion = usePrefersReducedMotion();
    const [animationStarted, setAnimationStarted] = useState(false);
    const hideMakeModel = partner && partner?.updateVehicleEnabled === false;

    useEffect(() => {
        const timeout = setTimeout(() => {
            setAnimationStarted(true);
        }, 800);

        return () => clearTimeout(timeout);
    }, []);

    if (
        activeStatus === ReservationUtils.ACTIVE_STATUS.ENDED ||
        paymentStatus === ReservationUtils.PAYMENT_STATUSES.REFUND
    ) {
        return null;
    }

    const handleInstructionsClick = e => {
        e.preventDefault();

        dispatch(
            push(
                `${window.location.pathname}${window.location.search}#parking-instructions`,
                {
                    method: 'replace',
                }
            )
        );
        onInstructionsClick('Review Parking Instructions');
    };

    const transition = prefersReducedMotion ? '' : 'all 300ms ease-in-out';

    if (showAllSet) {
        return (
            <Flex {...containerStyles} overflow="hidden" position="relative">
                <Box
                    top={0}
                    left={-80}
                    right={-80}
                    bottom={0}
                    zIndex={1}
                    position="absolute"
                    backgroundColor="success"
                    transformOrigin="center left"
                    transition={transition}
                    transform={
                        animationStarted ? 'rotate(-30deg)' : 'rotate(0deg)'
                    }
                    pointerEvents="none"
                />
                <Text
                    opacity={animationStarted ? '0%' : '100%'}
                    color="white"
                    fontSize="lg"
                    transition={transition}
                    fontWeight="semibold"
                    zIndex={1}
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    pointerEvents="none"
                >
                    You&apos;re all set!
                </Text>
                <Box {...leftAreaStyles} backgroundColor="green.100">
                    <Icon
                        width="1.125rem"
                        height="auto"
                        as={IconCheck}
                        color="success"
                        transition={transition}
                        transform={
                            animationStarted
                                ? 'translateY(0rem)'
                                : 'translateY(3.75rem)'
                        }
                        opacity={animationStarted ? '100%' : '0%'}
                    />
                </Box>
                <Box {...textContainerStyles}>
                    <Text
                        {...topTextStyles}
                        color="success"
                        transition={transition}
                        transform={
                            animationStarted
                                ? 'translateY(0rem)'
                                : 'translateY(1.75rem)'
                        }
                    >
                        You&apos;re all set!
                    </Text>
                    <Text
                        {...bottomTextStyles}
                        transform={
                            animationStarted
                                ? 'translateY(0rem)'
                                : 'translateY(2.25rem)'
                        }
                        transition={transition}
                    >
                        Make sure to{' '}
                        <Link
                            onClick={handleInstructionsClick}
                            href="#parking-instructions"
                        >
                            review the Parking Instructions
                        </Link>{' '}
                        before entering!
                    </Text>
                </Box>
            </Flex>
        );
    }

    if (!isPhoneOkay && !isVehicleOkay) {
        return (
            <Alert
                title="Don't Get Towed"
                description="You are at risk of being towed. Add your missing info before parking."
                status="error"
                marginTop={4}
                sx={{
                    '@media print': {
                        shadow: 'none',
                    },
                }}
            />
        );
    }

    if (!isPhoneOkay) {
        return (
            <Alert
                title="Add Phone Number"
                description="Add your phone number in case we need to contact you about your reservation."
                status="warning"
                marginTop={4}
                sx={{
                    '@media print': {
                        shadow: 'none',
                    },
                }}
            />
        );
    }

    return (
        <Flex {...containerStyles}>
            <Box {...leftAreaStyles} backgroundColor="red.100">
                <Icon
                    width="1.125rem"
                    height="auto"
                    as={IconExclamationCircle}
                    color="error"
                />
            </Box>
            <Box {...textContainerStyles}>
                <Text {...topTextStyles} color="error">
                    Don&apos;t Get Towed
                </Text>
                <Text {...bottomTextStyles}>
                    You are at risk of being towed. Add your license plate
                    before parking.
                </Text>
                <Button
                    {...buttonProps}
                    sx={{
                        '@media print': {
                            display: 'none',
                        },
                    }}
                    fontFamily="body"
                    width="100%"
                    marginTop={4}
                    variant="primary"
                >
                    {hideMakeModel ? 'Add License Plate' : 'Add Vehicle'}
                </Button>
            </Box>
        </Flex>
    );
};

NotificationBox.propTypes = {
    vehicleRequired: PropTypes.bool.isRequired,
    vehicle: PROPTYPE_RESERVATION_VEHICLE,
    activeStatus: PropTypes.oneOf(
        Object.values(ReservationUtils.ACTIVE_STATUS)
    ),
    paymentStatus: PROPTYPE_RESERVATION_PAYMENT_STATUS,
    onInstructionsClick: PropTypes.func.isRequired,
    phoneNumberRequired: PropTypes.bool.isRequired,
    phoneNumber: PropTypes.string,
    partner: PROPTYPE_RESERVATION_PARTNER,
};

export default NotificationBox;
